import {
  BaseChatClient,
  ConfirmOrderChatAttachmentPayload,
  CreateOrderChatAttachmentIntentPayload,
  CreateOrderChatAttachmentIntentResponse,
  GetOrderChatMessagesPayload,
  MarkOrderChatReadPayload,
  MarkOrderChatReadResult,
  OrderChat,
  OrderChatMessage,
  OrderChatMessagesPage,
  OrderChatUnreadSummary,
  SendOrderChatMessagePayload,
} from "@core/types";
import { supabaseClient } from "../supabase";
import {
  convertOrderChat,
  convertOrderChatMessage,
  convertOrderChatMessagesPage,
  convertOrderChatUnreadSummary,
} from "./converters";

const DEFAULT_CONTENT_TYPE = "application/octet-stream";

class ChatClient extends BaseChatClient {
  private async invoke<TResponse>(name: string, body?: Record<string, unknown>): Promise<TResponse> {
    const { data, error } = await supabaseClient.functions.invoke<TResponse>(name, {
      body,
    });

    if (error) throw error;
    return data as TResponse;
  }

  async getOrCreateOrderChat(orderId: number, options?: { create?: boolean }): Promise<OrderChat | null> {
    const data = await this.invoke<unknown>("chat-get-or-create", {
      orderId,
      create: options?.create ?? true,
    });

    return data ? convertOrderChat(data) : null;
  }

  async getOrderChatMessages(params: GetOrderChatMessagesPayload): Promise<OrderChatMessagesPage> {
    const data = await this.invoke<unknown>("chat-messages", {
      chatId: params.chatId,
      beforeCreatedAt: params.before?.createdAt,
      beforeId: params.before?.id,
      limit: params.limit,
    });

    return convertOrderChatMessagesPage(data);
  }

  async sendOrderChatMessage(payload: SendOrderChatMessagePayload): Promise<OrderChatMessage> {
    const data = await this.invoke<unknown>("chat-send-message", payload);
    return convertOrderChatMessage(data);
  }

  async createAttachmentIntent(
    payload: CreateOrderChatAttachmentIntentPayload,
  ): Promise<CreateOrderChatAttachmentIntentResponse> {
    const { chatId, file } = payload;

    return this.invoke<CreateOrderChatAttachmentIntentResponse>("chat-attachment-intent", {
      chatId,
      fileName: file.name,
      mimeType: file.type || DEFAULT_CONTENT_TYPE,
      fileSize: file.size,
    });
  }

  async uploadAttachmentFile(intent: CreateOrderChatAttachmentIntentResponse, file: File): Promise<void> {
    const { error } = await supabaseClient.storage
      .from(intent.bucket)
      .uploadToSignedUrl(intent.storagePath, intent.token, file, {
        contentType: file.type || DEFAULT_CONTENT_TYPE,
        upsert: false,
      });

    if (error) throw error;
  }

  async getRealtimeSessionToken(): Promise<{ token: string; expiresAt: number }> {
    return this.invoke<{ token: string; expiresAt: number }>("chat-session-token");
  }

  async confirmAttachment(payload: ConfirmOrderChatAttachmentPayload): Promise<OrderChatMessage> {
    const data = await this.invoke<{ message?: unknown } | unknown>("chat-attachment-confirm", payload);
    const raw = (data as { message?: unknown })?.message ?? data;
    return convertOrderChatMessage(raw);
  }

  async markOrderChatRead(payload: MarkOrderChatReadPayload): Promise<MarkOrderChatReadResult> {
    const data = await this.invoke<{ id: number; customer_last_read_at: string }>(
      "chat-mark-read",
      payload,
    );

    return {
      chatId: data?.id,
      customerLastReadAt: data?.customer_last_read_at ?? null,
    };
  }

  async getUnreadSummary(): Promise<OrderChatUnreadSummary> {
    const data = await this.invoke<unknown>("chat-unread-summary");
    return convertOrderChatUnreadSummary(data);
  }
}

export default ChatClient;
